<template>
  <div class="flex flex-col h-full w-full">
    <LoadingView v-if="isLoading" />
    <header class="flex items-center justify-between gap-4 flex-shrink-0 w-full">
      <h1 class="flex-0 text-3xl leading-9 font-headline font-medium antialiased  bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
        Floorplans
      </h1>
    </header>
    <div class="flex flex-col mt-4 p-4 rounded-lg">
      <div class="inline-flex flex-col flex-wrap md:flex-row md:justify-start gap-6">
        <div class="mb-4 flex-initial">
          <label for="search" class="block text-sm font-medium leading-5 pb-1.5 text-blue-500">Search by Keyword</label>
          <SearchInput v-model="query.search" color="gray" placeholder="Search..." />
        </div>
        <div class="mb-4 flex-initial">
          <SelectMultipleField v-model="query.status" label="Published" :options="Statuses.statuses" all="All Statuses" />
        </div>
        <div v-if="communities.length" class="mb-4 flex-initial">
          <SelectMultipleField v-model="query.community" label="Community" :options="communities.map(community => ({ label: community.meta?.info?.name ?? community.kova?.WebGroupName, value: community }))" empty="All Communities" all="All Communities" />
        </div>
        <div v-if="collections.length > 1" class="mb-4 flex-initial">
          <SelectMultipleField v-model="query.collection" label="Collection" :options="collections.map(collection => ({label: collection.kova?.MarketingName, value: collection.community_rid}))" empty="All Collections" all="All Collections" />
        </div>
        <div class="mb-4 flex-initial">
          <SelectMultipleField v-model="query.saleStatus" label="Sales Status" :options="FloorplanStatuses.statuses" all="All Sales Statuses" empty="All Sales Statuses" />
        </div>
        <div v-if="hasFilters" class="mb-4 flex-initial sm:mt-5">
          <FormButton @click="clear" class="bg-bhav-blue-500 text-white">Clear Filters</FormButton>
        </div>
        <div class="mb-4 flex-initial sm:mt-6 ml-auto">
          <button @click="showColumns = !showColumns" class="w-9 h-9 items-center justify-center bg-transparent hover:bg-bhav-cool-gray-50 text-bhav-blue-200 hover:text-bhav-blue-500 rounded-lg text-lg">
            <p class="relative">
              <span class="fak fa-col-options leading-none"></span>
              <span class="sr-only">Open Table Column Preferences Drawer</span>
            </p>
          </button>
        </div>
      </div>

    <PaginationTable empty-message="There are currently no floorplan items." v-if="!isLoading" :legacy="false" :data="filtered" :show-columns="showColumns"  @closeDrawer="showColumns = false" :columns="columns" view="floorplan-list">

      <template #col-name="{ row }">
        <router-link class="block whitespace-nowrap" :to="link(row)">
          <div class="flex items-center">
            <div class="flex-shrink-0 rounded-full overflow-hidden">
              <img v-if="row.image" class="h-10 w-10 aspect-square object-cover" :src="row.image" alt="">
              <i v-else class="fa-duotone fa-floorplan fa-2xl"></i>
            </div>
            <div class="px-4">
              <div class="text-sm leading-5 font-medium text-bhav-blue-500">
                {{ row.meta.info?.name || row.kova?.MarketingName }}
                <span v-if="row.status == 'new'"
                  class="bg-bhav-cool-gray-50 text-bhav-blue-500 px-2 rounded-full inline-block text-xs leading-5 ml-1">{{ Statuses.label('new') }}</span>
              </div>
            </div>
          </div>
        </router-link>
      </template>

      <template #col-collection="{ row }">
        <router-link class="block whitespace-nowrap" :to="link(row)">
          <div class="text-sm leading-5 text-bhav-blue-500">
            {{ row.collection_name }}
          </div>
        </router-link>
      </template>

      <template #col-price="{ row }">
        <router-link class="block whitespace-nowrap py-4" :to="link(row)">
          <div class="text-sm leading-5 text-bhav-blue-500">
            {{ formatToCurrency(row.meta?.info?.price?.min || row.kova?.Price) }}
          </div>
        </router-link>
      </template>

      <template #col-modelid="{ row }">
        <router-link class="block whitespace-nowrap py-4" :to="link(row)">
          <div class="text-sm leading-5 text-bhav-blue-500">
            {{ row.kova?.ModelID }}
          </div>
        </router-link>
      </template>

      <template #col-status="{ row }">
        <router-link class="block whitespace-nowrap" :to="link(row)">
          <span class="px-3 h-8 items-center justify-center inline-flex text-xs leading-5 font-semibold rounded-3xl"
            :class="['Internal', 'Off Market', 'Sold Out', 'N/S'].includes(row.kova.CustomFields.TextCommModelStatus1) ? 'bg-bhav-red-100 text-bhav-red-500' : 'bg-bhav-green-100 text-bhav-green-600'">
            {{ FloorplanStatuses.label(row.kova.CustomFields.TextCommModelStatus1) }}
          </span>
        </router-link>
      </template>

      <template #col-bedrooms="{ row }">
        <router-link class="block whitespace-nowrap" :to="link(row)">
          {{ getRange(row.meta.info.bedrooms.min || '-', row.meta.info.bedrooms.max || '-') }}
        </router-link>
      </template>

      <template #col-bathrooms="{ row }">
        <router-link class="block whitespace-nowrap" :to="link(row)">
          {{ getRange(((row.meta.info.bathrooms.min.full || 0) * 1) + ((row.meta.info.bathrooms.min.half || 0) * .5), ((row.meta.info.bathrooms.max.full || 0) * 1) + ((row.meta.info.bathrooms.max.half || 0) * .5)) }}
        </router-link>
      </template>

      <template #col-garages="{ row }">
        <router-link class="block whitespace-nowrap" :to="link(row)">
          {{ getRange(row.meta.info.garages.min || '-', row.meta.info.garages.max || '-') }}
        </router-link>
      </template>
      
      <template #col-sqft="{ row }">
        <router-link class="block whitespace-nowrap" :to="link(row)">
          {{ getRange(row.meta.info.sqft.min || '-', row.meta.info.sqft.max || '-') }}
        </router-link>
      </template>

      <template #col-published="{ row }">
        <div class="flex items-center gap-2">
          <ToggleButton v-if="GlobalPolicy.edit()" :id="`item-${row.id}-status`" @changed="save(row)" :checked="row.status == 'active'"
            on-color="green"
            :label="Statuses.label(row.status)"/>
            <template v-else>
              <p title="Published" class="text-bhav-blue fas fa-square-check text-xl" v-if="row.status == 'active'"></p>
              <p title="Unpublished" class="text-bhav-cool-gray-500 fas fa-square-x text-xl" v-else></p>
            </template>
        </div>
      </template>

    </PaginationTable>
  </div>
  </div>

</template>

<script>
import axios from "axios";
import { formatToCurrency, getRange } from '@/helpers/numberHelpers';
import LoadingView from '@/components/LoadingView.vue';
import { ToggleButton, SelectField, SelectMultipleField, FormButton, SearchInput } from '@/components/form';
import { mapActions, mapState, mapWritableState } from 'pinia';
import { useFloorplanStore } from "@/stores/floorplans";
import Statuses from "@/enums/Statuses";
import FloorplanStatuses from "@/enums/FloorplanStatuses";
import PaginationTable from "@/components/PaginationTable.vue";
import { GlobalPolicy } from "@/policies/global";

export default {
  setup() {
        return {
            GlobalPolicy,
            Statuses,
            FloorplanStatuses,
            getRange,
            link: (row) => ({ name: 'floorplan-show', params: { id: row.id } }),
            columns: [
              {
                name: 'name',
                label: 'Name',
                sort: (row) => row.meta.info?.name || row.kova?.MarketingName || "",
                default: true
              },
              {
                name: 'modelid',
                label: 'Model ID',
                sort: (row) => row.kova?.ModelID || "",
                default: true
              },
              {
                name: 'collection',
                label: 'Collection',
                sort: (row) => row.collection_name,
                default: true
              },
              {
                name: 'price',
                label: 'Base Price',
                sort: (row) => row.meta.info?.price?.min || row.kova?.Price || 0,
                default: true
              },
              {
                name: 'status',
                label: 'Sales Status',
                sort: (row) => FloorplanStatuses.label(row.kova.CustomFields.TextCommModelStatus1),
                default: true
              },
              {
                name: 'bedrooms',
                label: 'Bedrooms',
                sort: (row) => row.meta.info.bedrooms.min || 0,
                default: false
              },
              {
                name: 'bathrooms',
                label: 'Bathrooms',
                sort: (row) => (row.meta.info.bathrooms.min.full || 0) + ((row.meta.info.bathrooms.min.half || 0) * .5),
                default: false
              },
              {
                name: 'garages',
                label: 'Garages',
                sort: (row) => row.meta.info.garages.min || 0,
                default: false
              },
              {
                name: 'sqft',
                label: 'Square Footage',
                sort: (row) => row.meta.info.sqft.min || 0,
                default: false
              },
              {
                name: 'published',
                label: 'Published',
                sort: (row) => Statuses.label(row.status),
                default: true
              }
            ]
        }
    },
  data() {
    return {
      floorplans: [],
      communities: [],
      isLoading: true,
      showColumns: false,
    }
  },
  methods: {
    ...mapActions(useFloorplanStore, ['clear']),
    index() {
      axios.get(`floorplans`)
        .then(res => {
          this.floorplans = res.data;
          this.indexCommunities();
        })
        .catch(e => {
          this.isLoading = false;
          throw e;
        });
    },
    indexCommunities() {
      axios
        .get(`divisions/communities`)
        .then((res) => {
          let collection_rids = new Set(this.floorplans.map(floorplan => floorplan.kova.CommunityRID));
          this.communities = res.data
            .map(community => {
              community.collections = community.collections.filter(collection => collection.status != 'inactive' || collection_rids.has(collection.community_rid));
              return community;
            })
            .filter(community => community.status != 'inactive' && community.collections.length > 0)
            .sort((a, b) => (a.meta?.info?.name || a.kova?.WebGroupName) > (b.meta?.info?.name || b.kova?.WebGroupName) ? 1 : -1);
          this.isLoading = false;
        })
        .catch((e) => {
          this.isLoading = false;
          throw e;
        });
    },
    formatToCurrency,
    save(item) {
      item.status = item.status != 'active' ? 'active' : 'inactive';
      axios.patch(`floorplans/${item.id}`, item)
        .then(res => {
          if (res.data?.id) {
            this.index();
          }
        })
        .catch(e => {
          throw e;
        });
    }
  },
  computed: {
    ...mapState(useFloorplanStore, ['hasFilters']),
    ...mapWritableState(useFloorplanStore, ['query', 'page']),
    filtered() {
      let results = this.floorplans;
      if (!this.query.status.includes(null)) {
        results = results.filter(item => this.query.status.includes(item.status));
      }

      if (this.query.collection.length > 0) {
        results = results.filter(item => this.query.collection.includes(item.kova.CommunityRID));
      }

      if (this.query.community.length > 0) {
        let collections = this.query.community.flatMap(community => community.collections.map(collection => collection.community_rid));
        results = results.filter(item => collections.includes(item.kova.CommunityRID))
      }

      if (this.query.saleStatus.length > 0) {
        results = results.filter(item => this.query.saleStatus.includes(item.kova.CustomFields.TextCommModelStatus1));
      }
      
      if (this.query.search != '') {
        const searchTerm = this.query.search.toLowerCase();
        results = results.filter(item => {
          const name = (item.meta?.info?.name || item.kova?.MarketingName || '').toLowerCase();
          const collection = (item.collection_name || '').toLowerCase();
          const price = (item.meta?.info?.price?.min || item.kova?.Price || '').toString().toLowerCase();
          const bathrooms = getRange(((item.meta.info.bathrooms.min.full || 0) * 1) + ((item.meta.info.bathrooms.min.half || 0) * .5), ((item.meta.info.bathrooms.max.full || 0) * 1) + ((item.meta.info.bathrooms.max.half || 0) * .5)).toString().toLowerCase();
          const bedrooms = getRange(item.meta?.info?.bedrooms?.min || '-', item.meta?.info?.bedrooms?.max || '-').toString().toLowerCase();
          const garages = getRange(item.meta?.info?.garages?.min || '-', item.meta?.info?.garages?.max || '-').toString().toLowerCase();
          const sqft =  getRange(item.meta?.info?.sqft?.min || '-', item.meta?.info?.sqft?.max || '-').toString().toLowerCase();

          return name.includes(searchTerm) ||
                 collection.includes(searchTerm) ||
                 price.includes(searchTerm) ||
                 bathrooms.includes(searchTerm) ||
                 bedrooms.includes(searchTerm) ||
                 garages.includes(searchTerm) ||
                 sqft.includes(searchTerm);
        });
      }
      return results;
    },
    collections() {
      if(this.communities.length == 0 || this.query.community.length == 0) return [];
      return this.query.community.flatMap(community => community.collections).sort((a, b) => (a.kova?.MarketingName) > (b.kova?.MarketingName) ? 1 : -1);
    },
  },
  watch: {
    "query.community": {
      handler(newVal, oldVal) {
        if(newVal.length !== oldVal.length && this.query.collection.length > 0) {
          let collections = newVal.flatMap(community => community.collections.map(collection => collection.community_rid));
          this.query.collection = this.query.collection.filter(collection => collections.includes(collection));
        }
      },
      deep: true
    },
    query: {
      handler() { this.page = 1; },
      deep: true
    }
  },
  created() {
    this.index();
  },
  components: { LoadingView, ToggleButton, SelectField, SelectMultipleField, FormButton, PaginationTable, SearchInput },
}
</script>